import { useScrollAnimationSection } from '../../../hooks/useScrollAnimation';
import { useNavigate } from 'react-router-dom';
import { articles } from '../../../mocks/articles';

interface EntryAction {
  icon: string;
  step: string;
  stepLabel: string;
  title: string;
  recommendation: string;
  bgColor: string;
  iconBg: string;
  iconColor: string;
  borderColor: string;
  accentColor: string;
  stepBadgeColor: string;
}

const entryActions: EntryAction[] = [
  {
    icon: 'ri-list-check-3',
    title: '家づくりの全体の流れを知る',
    recommendation: '「全体像を把握してから動きたい」という方',
    step: '01',
    stepLabel: 'STEP 01',
    bgColor: 'bg-[#FAF8F4]',
    iconBg: 'bg-[#EAE2D4]',
    iconColor: 'text-[#8A7A5A]',
    borderColor: 'border-[#D8CCBA]',
    accentColor: 'text-[#6E6040]',
    stepBadgeColor: 'bg-[#EAE2D4] text-[#6E6040]',
  },
  {
    icon: 'ri-wallet-3-line',
    title: '家づくりの費用を知る',
    recommendation: '「いくらかかるか、まず数字を知りたい」という方',
    step: '02',
    stepLabel: 'STEP 02',
    bgColor: 'bg-[#FEF7EC]',
    iconBg: 'bg-[#F5E4C0]',
    iconColor: 'text-[#C8903A]',
    borderColor: 'border-[#EDD8A8]',
    accentColor: 'text-[#A87430]',
    stepBadgeColor: 'bg-[#F5E4C0] text-[#A87430]',
  },
  {
    icon: 'ri-community-line',
    title: '住宅会社を比較する',
    recommendation: '「HM・工務店の違いが分からない」という方',
    step: '03',
    stepLabel: 'STEP 03',
    bgColor: 'bg-[#EFF5ED]',
    iconBg: 'bg-[#D4E8CE]',
    iconColor: 'text-[#5E8F56]',
    borderColor: 'border-[#BDD8B4]',
    accentColor: 'text-[#4E7A48]',
    stepBadgeColor: 'bg-[#D4E8CE] text-[#4E7A48]',
  },
  {
    icon: 'ri-map-2-line',
    title: '土地探しから始める',
    recommendation: '「土地が先？家が先？まず何をすれば？」という方',
    step: '04',
    stepLabel: 'STEP 04',
    bgColor: 'bg-[#F5EFE8]',
    iconBg: 'bg-[#E8D8C4]',
    iconColor: 'text-[#956840]',
    borderColor: 'border-[#D8C4A8]',
    accentColor: 'text-[#7A5630]',
    stepBadgeColor: 'bg-[#E8D8C4] text-[#7A5630]',
  },
];

export default function EntryActionSection() {
  const sectionRef = useScrollAnimationSection();
  const navigate = useNavigate();

  const handleClick = (step: string) => {
    const found = articles.find((a) => a.step === step);
    if (found) navigate(`/articles/${found.id}`);
  };

  return (
    <section
      id="entry-action"
      ref={sectionRef}
      className="py-20 md:py-28 bg-[#F5F1ED]"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-12 fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#E8DDD0] text-[#8A7040] text-xs font-semibold tracking-wide mb-4">
            <i className="ri-compass-3-line" aria-hidden="true" />
            入口を選ぶ
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-[#2C2C2C] leading-tight mb-3">
            まず、あなたに合った入口から<br className="md:hidden" />始めよう
          </h2>
          <p className="text-[#6B6B6B] text-sm md:text-base leading-relaxed max-w-lg mx-auto">
            家づくりは人によって「一番気になること」が違います。<br className="hidden md:block" />
            あなたが今一番知りたいテーマから読み始めてください。
          </p>
        </div>

        {/* Action Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {entryActions.map((action, index) => (
            <button
              key={action.title}
              type="button"
              onClick={() => handleClick(action.step)}
              className={`fade-in-up group ${action.bgColor} border ${action.borderColor} rounded-2xl p-6 text-left cursor-pointer hover:-translate-y-1 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#8FA882]`}
              style={{ transitionDelay: `${index * 80}ms` }}
            >
              {/* Step badge + Icon row */}
              <div className="flex items-start justify-between mb-4">
                <div className={`w-14 h-14 flex items-center justify-center rounded-xl ${action.iconBg}`}>
                  <i className={`${action.icon} text-2xl ${action.iconColor}`} aria-hidden="true" />
                </div>
                <span className={`text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-full ${action.stepBadgeColor}`}>
                  {action.stepLabel}
                </span>
              </div>

              {/* Title */}
              <h3 className="text-[#2C2C2C] text-base font-bold leading-snug mb-3 group-hover:opacity-80 transition-opacity">
                {action.title}
              </h3>

              {/* Recommendation */}
              <p className="text-[#6B6B6B] text-xs leading-relaxed mb-4">
                <strong className="text-[#2C2C2C] font-semibold">こんな方に：</strong><br />
                {action.recommendation}
              </p>

              {/* CTA */}
              <div className={`inline-flex items-center gap-1.5 text-xs font-bold ${action.accentColor} group-hover:gap-3 transition-all duration-200 whitespace-nowrap`}>
                記事を読む
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </div>
            </button>
          ))}
        </div>

        {/* Footer note */}
        <div className="fade-in-up mt-8 text-center">
          <p className="text-[#A0A0A0] text-xs">
            どれを選べばいいか迷ったら
            <button
              type="button"
              onClick={() => handleClick('01')}
              className="text-[#8FA882] font-semibold ml-1 cursor-pointer hover:underline whitespace-nowrap"
            >
              「家づくりの全体の流れを知る」
            </button>
            から始めるのがおすすめです
          </p>
        </div>
      </div>
    </section>
  );
}
